import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Empty, Flex } from 'antd';
import {
  getAllCourses,
  getAllTrainingAssessments,
} from '../services/ApiActions/lmsAct';
import CourseCard from '../components/LMSComponent/CourseCard';
import Loader from '../components/Layout/Loader';
import PageHeading from '../components/Layout/PageHeading';

// Types
interface ApiResponse<T> {
  status: 'success' | 'error';
  data: T;
}

interface Course {
  id: string;
  name: string;
  thumbnail?: { url: string };
  percentage?: number;
  moduleCount?: number;
  isAssessment?: boolean;
}

interface CourseData {
  course: Course[];
}

type TrainingAssessmentData = Course[];

/**
 * LMSPage displays a list of available courses and training assessments
 */
const LMSPage = () => {
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);

  // Data fetching
  const fetchCoursesAndAssessments = async () => {
    try {
      setIsLoading(true);

      const [coursesRes, assessmentsRes] = await Promise.all([
        getAllCourses() as Promise<ApiResponse<CourseData>>,
        getAllTrainingAssessments() as Promise<
          ApiResponse<TrainingAssessmentData>
        >,
      ]);

      // Process regular courses
      const courseData: Course[] =
        coursesRes.status === 'success' && coursesRes.data.course
          ? coursesRes.data.course
          : [];

      // Process training assessments
      const assessmentData: Course[] =
        assessmentsRes.status === 'success' &&
        Array.isArray(assessmentsRes.data)
          ? assessmentsRes.data.map((assessment) => ({
              ...assessment,
              isAssessment: true,
            }))
          : [];

      // Combine both data sources
      setCourses([...courseData, ...assessmentData]);
    } catch (error) {
      console.error('Error fetching courses and assessments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Event handlers
  const handleCourseClick = (course: Course) => {
    if (course.isAssessment) {
      navigate(`/lms/training-assessment/${course.id}`);
    } else {
      navigate(`/lms/course/${course.id}`);
    }
  };

  // Effects
  useEffect(() => {
    fetchCoursesAndAssessments();
  }, []);

  // Components
  const renderCourses = () => {
    if (courses.length === 0) {
      return (
        <Empty
          style={{ marginTop: '5em' }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Typography.Text>No courses available.</Typography.Text>}
        />
      );
    }

    return (
      <Flex wrap="wrap" gap="16px">
        {courses.map((course) => (
          <CourseCard
            key={course.id}
            title={course.name}
            imageUrl={course.thumbnail?.url || ''}
            completionPercentage={course.percentage?.toString() || '0'}
            count={course.moduleCount || 0}
            cardType="module"
            isAssessment={course.isAssessment}
            onPlayButtonClick={() => handleCourseClick(course)}
          />
        ))}
      </Flex>
    );
  };

  // Loading state
  if (isLoading) {
    return <Loader style={{ marginTop: '20%', justifyContent: 'center' }} />;
  }

  return (
    <div className="lms-page">
      <PageHeading heading="Learning Management System" />
      {renderCourses()}
    </div>
  );
};

export default LMSPage;
